import { useEffect, useRef, useState } from "react";
import { MapPin, X } from "lucide-react";
import { api, errorMessage } from "../lib/api";

export const PlaceAreaInput = ({ csrfToken, onChoose, onTyping, disabled }) => {
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);
  const [chosen, setChosen] = useState(false);
  const token = useRef(null);
  const revision = useRef(0);
  const headers = { "X-CSRF-Token": csrfToken };

  useEffect(() => {
    if (disabled || chosen || text.trim().length < 2) { setSuggestions([]); return undefined; }
    const current = ++revision.current;
    const timer = window.setTimeout(async () => {
      setBusy(true); setError("");
      try {
        if (!token.current) {
          const { data } = await api.post("/map/place-session", {}, { headers });
          if (revision.current !== current) return;
          token.current = data.session_token;
        }
        const { data } = await api.post("/map/place-suggestions", {
          text: text.trim(), session_token: token.current }, { headers });
        if (revision.current === current) { setSuggestions(data.items); setActive(0); }
      } catch (e) { if (revision.current === current) { setError(errorMessage(e)); token.current = null; } }
      finally { if (revision.current === current) setBusy(false); }
    }, 300);
    return () => { window.clearTimeout(timer); revision.current++; };
  }, [text, chosen, disabled, csrfToken]);

  const select = async (item) => {
    const current = ++revision.current;
    setBusy(true); setError(""); setSuggestions([]);
    try {
      const { data } = await api.post("/map/place-location", { place_id: item.place_id,
        session_token: token.current }, { headers });
      if (revision.current !== current) return;
      token.current = null; setChosen(true); setText(item.label);
      onChoose({ center: data.center, viewport: data.viewport, label: item.main || item.label });
    } catch (e) { if (revision.current === current) { setError(errorMessage(e)); token.current = null; } }
    finally { if (revision.current === current) setBusy(false); }
  };
  const change = (value) => { if (chosen) { setChosen(false); token.current = null; }
    onTyping(); setText(value); setError(""); };
  const clear = () => { revision.current++; token.current = null; setText(""); setChosen(false);
    setSuggestions([]); setError(""); onTyping(); };
  return <div className="place-area-search" data-testid="place-area-search">
    <div className="place-input-row"><span className="search-step-index" data-testid="search-area-step">1/2</span>
      <input type="search" value={text} disabled={disabled} placeholder="Città o indirizzo"
        aria-label="Cerca città o indirizzo sulla mappa" autoComplete="off" onChange={(e) => change(e.target.value)}
        onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(suggestions.length - 1, i + 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
          if (e.key === "Enter" && suggestions[active]) { e.preventDefault(); select(suggestions[active]); }
          if (e.key === "Escape") setSuggestions([]); }} data-testid="area-place-input" />
      {text && <button type="button" onClick={clear} aria-label="Cancella luogo" data-testid="area-place-clear"><X size={18} /></button>}
    </div>
    {busy && <span className="place-search-status" role="status" data-testid="area-place-loading">Ricerca luogo…</span>}
    {error && <span className="place-search-status error" role="alert" data-testid="area-place-error">{error}</span>}
    {!!suggestions.length && <div className="place-suggestions" role="listbox" aria-label="Luoghi suggeriti" data-testid="area-place-suggestions">
      {suggestions.map((item, index) => <button type="button" role="option" aria-selected={active === index}
        key={item.place_id} onClick={() => select(item)} data-testid={`area-suggestion-${index}`}>
        <MapPin size={17} aria-hidden="true" /><span><strong>{item.main}</strong><small>{item.label}</small></span></button>)}
    </div>}
  </div>;
};